// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import {makePersistent} from '@app/features/platform/utils/MobXPersistence';
import {getStreamKey} from '@app/features/voice/components/StreamKeys';
import VoiceConnectionManager from '@app/features/voice/engine/VoiceConnectionManager';
import {getEffectiveAudioState} from '@app/features/voice/engine/VoiceEffectiveAudioState';
import {VoiceTrackKind, VoiceTrackSource} from '@app/features/voice/engine/VoiceTrackSource';
import {getRemoteVoicePlaybackBoost} from '@app/features/voice/state/RemoteVoicePlaybackBoost';
import StreamAudioPrefs from '@app/features/voice/state/StreamAudioPrefs';
import VoiceSettings from '@app/features/voice/state/VoiceSettings';
import {
	boostedVoiceVolumePercentToTrackVolume,
	clampVoiceVolumePercent,
} from '@app/features/voice/utils/VoiceVolumeUtils';
import type {RemoteAudioTrack, RemoteParticipant, Room} from 'livekit-client';
import {makeAutoObservable} from 'mobx';

const logger = new Logger('ParticipantVolume');
const isRemoteAudioTrack = (track: unknown): track is RemoteAudioTrack =>
	track != null &&
	typeof track === 'object' &&
	'kind' in track &&
	(track as {kind?: unknown}).kind === VoiceTrackKind.Audio;
const idUser = (identity: string): string | null => {
	const m = identity.match(/^user_(\d+)(?:_(.+))?$/);
	return m ? m[1] : null;
};
const idConnection = (identity: string): string | null => {
	const match = identity.match(/^user_(\d+)_(.+)$/);
	return match ? match[2] : null;
};

function composeVolumePercent(...volumeParts: Array<number>): number {
	const composed = volumeParts.reduce((accumulator, currentValue) => {
		return accumulator * (clampVoiceVolumePercent(currentValue) / 100);
	}, 100);
	return clampVoiceVolumePercent(composed);
}

class ParticipantVolume {
	volumes: Record<string, number> = {};
	localMutes: Record<string, boolean> = {};
	connectionVolumesByLocalConnectionId: Record<string, Record<string, number>> = {};

	constructor() {
		makeAutoObservable(
			this,
			{
				getVolume: false,
				isLocalMuted: false,
				getConnectionVolume: false,
			},
			{autoBind: true},
		);
		void this.initPersistence();
	}

	private async initPersistence(): Promise<void> {
		await makePersistent(this, 'ParticipantVolume', ['volumes', 'localMutes']);
	}

	setVolume(userId: string, volume: number): void {
		const clamped = clampVoiceVolumePercent(volume);
		this.volumes = {
			...this.volumes,
			[userId]: clamped,
		};
		logger.debug(`Set volume for ${userId}: ${clamped}`);
	}

	setLocalMute(userId: string, muted: boolean): void {
		this.localMutes = {
			...this.localMutes,
			[userId]: muted,
		};
		logger.debug(`Set local mute for ${userId}: ${muted}`);
	}

	setConnectionVolume(connectionId: string, volume: number): void {
		const localConnectionId = VoiceConnectionManager.connectionId;
		if (!localConnectionId || !connectionId) {
			return;
		}
		const clamped = clampVoiceVolumePercent(volume);
		const existingBucket = this.connectionVolumesByLocalConnectionId[localConnectionId] ?? {};
		this.connectionVolumesByLocalConnectionId = {
			...this.connectionVolumesByLocalConnectionId,
			[localConnectionId]: {
				...existingBucket,
				[connectionId]: clamped,
			},
		};
		logger.debug(`Set connection volume for ${connectionId} from ${localConnectionId}: ${clamped}`);
	}

	getVolume(userId: string): number {
		return clampVoiceVolumePercent(this.volumes[userId] ?? 100);
	}

	getConnectionVolume(connectionId: string | null): number {
		if (!connectionId) {
			return 100;
		}
		const localConnectionId = VoiceConnectionManager.connectionId;
		if (!localConnectionId) {
			return 100;
		}
		const bucket = this.connectionVolumesByLocalConnectionId[localConnectionId];
		return clampVoiceVolumePercent(bucket?.[connectionId] ?? 100);
	}

	isLocalMuted(userId: string): boolean {
		return this.localMutes[userId] ?? false;
	}

	resetUserSettings(userId: string): void {
		const newVolumes = {...this.volumes};
		const newLocalMutes = {...this.localMutes};
		delete newVolumes[userId];
		delete newLocalMutes[userId];
		this.volumes = newVolumes;
		this.localMutes = newLocalMutes;
		logger.debug(`Reset settings for ${userId}`);
	}

	applySettingsToRoom(room: Room | null): void {
		if (!room) return;
		room.remoteParticipants.forEach((participant) => {
			this.applySettingsToParticipant(participant);
		});
	}

	applySettingsToParticipant(participant: RemoteParticipant): void {
		const userId = idUser(participant.identity);
		if (!userId) return;
		const connectionId = idConnection(participant.identity);
		const streamKey = connectionId
			? getStreamKey(VoiceConnectionManager.guildId, VoiceConnectionManager.channelId, connectionId)
			: null;
		const userVolume = this.getVolume(userId);
		const connectionVolume = this.getConnectionVolume(connectionId);
		const outputVolume = VoiceSettings.getOutputVolume();
		const locallyMuted = this.isLocalMuted(userId);
		const {effectiveDeaf} = getEffectiveAudioState();
		participant.audioTrackPublications.forEach((pub) => {
			try {
				const isScreenShareAudio = pub.source === VoiceTrackSource.ScreenShareAudio;
				const streamVolume = streamKey ? StreamAudioPrefs.getVolume(streamKey) : 100;
				const streamMuted = streamKey ? StreamAudioPrefs.isMuted(streamKey) : false;
				const track = pub.track;
				if (isRemoteAudioTrack(track)) {
					const baseVolume = isScreenShareAudio
						? boostedVoiceVolumePercentToTrackVolume(composeVolumePercent(streamVolume, outputVolume))
						: boostedVoiceVolumePercentToTrackVolume(composeVolumePercent(userVolume, connectionVolume, outputVolume));
					const playbackBoost = isScreenShareAudio ? 1 : getRemoteVoicePlaybackBoost(participant.identity);
					const nextVolume = Math.max(0, Math.min(3, baseVolume * playbackBoost));
					track.setVolume(nextVolume);
				}
				const shouldDisable = isScreenShareAudio ? locallyMuted || streamMuted : locallyMuted || effectiveDeaf;
				if (isScreenShareAudio) {
					logger.debug('Applying screen share audio prefs', {
						participantIdentity: participant.identity,
						trackSid: pub.trackSid,
						streamKey,
						streamVolume,
						streamMuted,
						locallyMuted,
						effectiveDeaf,
						shouldDisable,
					});
				}
				if (pub.isDesired) {
					pub.setEnabled(!shouldDisable);
				}
				if (isScreenShareAudio && streamKey && StreamAudioPrefs.hasEntry(streamKey)) {
					StreamAudioPrefs.touchStream(streamKey);
				}
			} catch (error) {
				logger.warn(`Failed to apply settings to participant ${userId}`, {error});
			}
		});
	}
}

export default new ParticipantVolume();
